import { useState } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import { BookmarkAdd, Check } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../../services/api';
import { useStudioStore } from '../stores/studio.store';

interface SaveToLibraryButtonProps {
  /**
   * Project asset id (the server-side studio_project_assets row id)
   * that should be copied into the canonical creatives library.
   * If undefined, the button is disabled.
   */
  assetId: string | undefined;
  /** Optional display name (defaults to the asset's prompt / filename). */
  defaultName?: string;
  /** Visual size of the button. Defaults to 26px. */
  size?: number;
  /**
   * Where to pin the overlay. Defaults to top-right.
   */
  corner?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

/**
 * A small overlay button that copies a project asset into the canonical
 * creatives library. Meant to be rendered on top of a result preview (image,
 * video, etc.) inside a canvas node.
 */
export default function SaveToLibraryButton({
  assetId,
  defaultName,
  size = 26,
  corner = 'top-right',
}: SaveToLibraryButtonProps) {
  const { enqueueSnackbar } = useSnackbar();
  const projectId = useStudioStore((s) => s.projectId);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!projectId || !assetId || saving || saved) return;
    setSaving(true);
    try {
      await api.post(
        `/studio/projects/${projectId}/assets/${assetId}/save-to-library`,
        defaultName ? { name: defaultName } : {}
      );
      setSaved(true);
      enqueueSnackbar('Saved to creatives library', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar((err as Error).message || 'Failed to save to library', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const positions: Record<string, Record<string, number>> = {
    'top-right': { top: 6, right: 6 },
    'top-left': { top: 6, left: 6 },
    'bottom-right': { bottom: 6, right: 6 },
    'bottom-left': { bottom: 6, left: 6 },
  };

  const disabled = !assetId || !projectId;

  return (
    <Tooltip
      title={
        disabled
          ? 'Not yet saved to project'
          : saved
            ? 'Saved to creatives library'
            : 'Save to creatives library'
      }
    >
      <span
        style={{
          position: 'absolute',
          ...positions[corner],
          zIndex: 2,
          display: 'inline-flex',
        }}
      >
        <IconButton
          size="small"
          onClick={handleClick}
          disabled={disabled}
          sx={{
            width: size,
            height: size,
            bgcolor: saved ? 'success.main' : 'rgba(0,0,0,0.55)',
            color: 'white',
            backdropFilter: 'blur(4px)',
            '&:hover': {
              bgcolor: saved ? 'success.dark' : 'rgba(0,0,0,0.8)',
            },
            '&.Mui-disabled': {
              bgcolor: 'rgba(0,0,0,0.3)',
              color: 'rgba(255,255,255,0.5)',
            },
          }}
        >
          {saving ? (
            <CircularProgress size={size * 0.55} sx={{ color: 'white' }} />
          ) : saved ? (
            <Check sx={{ fontSize: size * 0.6 }} />
          ) : (
            <BookmarkAdd sx={{ fontSize: size * 0.6 }} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
}
